import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "@/lib/router";
import { usePractice } from "../context/PracticeContext";
import { useBreadcrumbs } from "../context/BreadcrumbContext";
import { useToast } from "../context/ToastContext";
import { ApiError } from "../api/client";
import {
  inboxApi,
  type InboxItem,
  type InboxItemAction,
  type InboxItemType,
} from "../api/inbox";
import { graphsApi } from "../api/graphs";
import { workItemsApi } from "../api/workItems";
import { queryKeys } from "../lib/queryKeys";
import { timeAgo } from "../lib/timeAgo";
import { EmptyState } from "../components/EmptyState";
import { PageSkeleton } from "../components/PageSkeleton";
import { Button } from "@/components/ui/button";
import { Tabs } from "@/components/ui/tabs";
import { PageTabBar } from "../components/PageTabBar";
import { cn } from "../lib/utils";
import {
  Inbox as InboxIcon,
  AlertTriangle,
  Stamp,
  CircleDot,
  Siren,
  CircleX,
  Workflow,
  Check,
  X,
  type LucideIcon,
} from "lucide-react";

const TYPE_META: Record<InboxItemType, { Icon: LucideIcon; label: string }> = {
  metric_alert: { Icon: AlertTriangle, label: "Alert" },
  approval: { Icon: Stamp, label: "Approval" },
  issue: { Icon: CircleDot, label: "Issue" },
  pj_escalation: { Icon: Siren, label: "Escalation" },
  work_item: { Icon: CircleX, label: "Failed step" },
  work_item_approval: { Icon: Workflow, label: "Approval" },
};

type GraphResumeAction = Extract<InboxItemAction, { kind: "graph_resume" }>;
type DismissAction = Extract<InboxItemAction, { kind: "work_item_dismiss" }>;
type ResumeArgs = { action: GraphResumeAction; decision: "approved" | "rejected" };

// Tailwind classes for the leading icon chip, keyed by severity.
const SEVERITY_CHIP: Record<InboxItem["severity"], string> = {
  critical:
    "bg-red-50 text-red-600 ring-1 ring-red-200 dark:bg-red-900/30 dark:text-red-300 dark:ring-red-800/40",
  warning:
    "bg-amber-50 text-amber-600 ring-1 ring-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:ring-amber-800/40",
  info:
    "bg-blue-50 text-blue-600 ring-1 ring-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:ring-blue-800/40",
};

function InboxRow({
  item,
  onOpen,
  onResume,
  onDismiss,
  pendingItemId,
}: {
  item: InboxItem;
  onOpen: (item: InboxItem) => void;
  onResume: (args: ResumeArgs) => void;
  onDismiss: (action: DismissAction) => void;
  pendingItemId?: string;
}) {
  const meta = TYPE_META[item.type];
  const clickable = item.link !== null;
  const pending = !!item.action && pendingItemId === item.action.itemId;
  return (
    <div className="flex flex-col">
      <button
        type="button"
        disabled={!clickable}
        onClick={() => onOpen(item)}
        className={cn(
          "group flex w-full items-start gap-3 px-3 py-3.5 text-left transition-colors sm:px-4",
          clickable ? "cursor-pointer hover:bg-accent/50" : "cursor-default",
        )}
      >
        {/* Unread dot — fixed-width column so titles stay aligned */}
        <span className="mt-2 flex w-2 shrink-0 justify-center">
          {item.unread && (
            <span className="h-2 w-2 rounded-full bg-blue-600 dark:bg-blue-400" />
          )}
        </span>

        {/* Severity-tinted type icon */}
        <span
          className={cn(
            "flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
            SEVERITY_CHIP[item.severity],
          )}
        >
          <meta.Icon className="h-4 w-4" />
        </span>

        {/* Title + subtitle */}
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="flex items-baseline gap-2">
            <span
              className={cn(
                "min-w-0 flex-1 truncate text-sm",
                item.unread ? "font-semibold text-foreground" : "text-foreground/90",
              )}
            >
              {item.title}
            </span>
            <span className="shrink-0 text-[11px] text-muted-foreground">
              {timeAgo(item.timestamp)}
            </span>
          </span>
          <span className="flex items-center gap-2">
            <span className="shrink-0 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              {meta.label}
            </span>
            {item.subtitle && (
              <>
                <span className="text-muted-foreground/50">·</span>
                <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
                  {item.subtitle}
                </span>
              </>
            )}
          </span>
        </span>
      </button>

      {/* Inline actions — aligned under the title, sibling to the row button so
          we never nest interactive controls. */}
      {item.action?.kind === "graph_resume" && (
        <div className="flex items-center gap-2 px-3 pb-3 pl-[3.25rem] sm:px-4 sm:pl-[3.75rem]">
          <Button
            size="sm"
            disabled={pending}
            onClick={() => onResume({ action: item.action as GraphResumeAction, decision: "approved" })}
          >
            <Check className="h-3.5 w-3.5" />
            Approve
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={pending}
            onClick={() => onResume({ action: item.action as GraphResumeAction, decision: "rejected" })}
          >
            <X className="h-3.5 w-3.5" />
            Reject
          </Button>
        </div>
      )}
      {item.action?.kind === "work_item_dismiss" && (
        <div className="flex items-center gap-2 px-3 pb-3 pl-[3.25rem] sm:px-4 sm:pl-[3.75rem]">
          <Button
            size="sm"
            variant="outline"
            disabled={pending}
            onClick={() => onDismiss(item.action as DismissAction)}
          >
            <X className="h-3.5 w-3.5" />
            Dismiss
          </Button>
        </div>
      )}
    </div>
  );
}

export function SimpleInbox() {
  const { selectedPracticeId } = usePractice();
  const { setBreadcrumbs } = useBreadcrumbs();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { pushToast } = useToast();

  const pathSegment = location.pathname.split("/").pop() ?? "new";
  const tab: "new" | "all" = pathSegment === "all" ? "all" : "new";
  const filter = tab === "new" ? "unread" : "all";

  useEffect(() => {
    setBreadcrumbs([{ label: "Inbox" }]);
  }, [setBreadcrumbs]);

  const { data: items, isLoading } = useQuery({
    queryKey: queryKeys.inbox(selectedPracticeId!, filter),
    queryFn: () => inboxApi.list(selectedPracticeId!, { filter, limit: 100 }),
    enabled: !!selectedPracticeId,
    refetchInterval: 60_000,
  });

  const { data: unread } = useQuery({
    queryKey: queryKeys.inbox(selectedPracticeId!, "count"),
    queryFn: () => inboxApi.count(selectedPracticeId!),
    enabled: !!selectedPracticeId,
    refetchInterval: 60_000,
  });

  const resume = useMutation({
    mutationFn: ({ action, decision }: ResumeArgs) =>
      graphsApi.resumeWorkItem(
        selectedPracticeId!,
        action.graphId,
        action.runId,
        action.itemId,
        decision,
      ),
    onSuccess: (_data, { decision }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inbox(selectedPracticeId!, filter) });
      queryClient.invalidateQueries({ queryKey: queryKeys.inbox(selectedPracticeId!, "count") });
      pushToast({
        title: decision === "approved" ? "Approved" : "Rejected",
        body: "The workflow step has been updated.",
        tone: "success",
      });
    },
    onError: (err) => {
      pushToast({
        title: "Couldn't record decision",
        body: err instanceof ApiError ? err.message : "Please try again.",
        tone: "error",
      });
    },
  });

  const dismiss = useMutation({
    mutationFn: (action: DismissAction) =>
      workItemsApi.dismiss(selectedPracticeId!, action.itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inbox(selectedPracticeId!, filter) });
      queryClient.invalidateQueries({ queryKey: queryKeys.inbox(selectedPracticeId!, "count") });
    },
    onError: (err) => {
      pushToast({
        title: "Couldn't dismiss item",
        body: err instanceof ApiError ? err.message : "Please try again.",
        tone: "error",
      });
    },
  });

  function openItem(item: InboxItem) {
    if (item.link) navigate(item.link);
  }

  if (!selectedPracticeId) {
    return <EmptyState icon={InboxIcon} message="Select a practice to view your inbox." />;
  }

  const unreadCount = unread?.count ?? 0;
  const pendingItemId = resume.isPending
    ? resume.variables?.action.itemId
    : dismiss.isPending
      ? dismiss.variables?.itemId
      : undefined;

  return (
    <div className="mx-auto max-w-3xl space-y-5 p-4 sm:p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Inbox</h1>
        <Tabs value={tab} onValueChange={(value) => navigate(`/inbox/${value === "all" ? "all" : "new"}`)}>
          <PageTabBar
            items={[
              {
                value: "new",
                label: (
                  <>
                    Unread
                    {unreadCount > 0 && (
                      <span className="ml-1.5 rounded-full bg-blue-500/20 px-1.5 py-0.5 text-[10px] font-medium text-blue-500">
                        {unreadCount}
                      </span>
                    )}
                  </>
                ),
              },
              { value: "all", label: "All" },
            ]}
          />
        </Tabs>
      </div>

      {isLoading && !items && <PageSkeleton variant="inbox" />}

      {items && items.length === 0 && (
        <EmptyState
          icon={InboxIcon}
          message={
            tab === "new"
              ? "You're all caught up — nothing needs your attention."
              : "Nothing in your inbox yet."
          }
        />
      )}

      {items && items.length > 0 && (
        <div className="divide-y divide-border rounded-xl border border-border bg-card">
          {items.map((item) => (
            <InboxRow
              key={item.id}
              item={item}
              onOpen={openItem}
              onResume={(args) => resume.mutate(args)}
              onDismiss={(action) => dismiss.mutate(action)}
              pendingItemId={pendingItemId}
            />
          ))}
        </div>
      )}
    </div>
  );
}
